import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { FaPlus, FaSync, FaTimes, FaLayerGroup } from "react-icons/fa";
import { BaseContext } from "../../../../../contexts/BaseContext";
import { metadataService, tagService } from "../../../../../services/api";
import { deasyFileKey } from "../../../../../config/config";
import {
  Table,
  TableContainer,
  TablePagination,
  Paper,
  CircularProgress,
} from "@mui/material";
import MetadataColumnSelector from "./components/MetadataColumnSelector";
import CreateDataSlice from "../../../../utils/CreateDataSlice";
import { WorkflowType } from "../../../../../models/WorkflowModel";
import DistributionChart from "./components/DistributionChart";
import {
  processTagValue,
  TABLE_COLUMN_WIDTHS,
  ActiveFilters,
  fetchChunkEvidence,
  handleDataSliceChange,
  extractTagsFromSchema,
} from "./metadataTableUtils";
import BlankMetadataScreen from "./BlankMetadataScreen";
import { nodePathToConditions } from "../DataDiscovery/GraphUtils";
import CompactDataSliceSelector from "./CompactDataSliceSelector";
import FilterMenu, { OPERATORS } from "./components/FilterMenu";
import EvidenceRow from "./components/EvidenceRow";
import TableBody from "./components/TableBody";
import TableHeader from "./components/TableHeader";
import { StandardizeValuesPanel } from "../DataDiscovery/Standardization/StandardizeValuesPanel";
import { toast } from "react-hot-toast";
import DeleteMetadataConfirmationModal from "./components/DeleteMetadataConfirmationModal";
import TagEvidence from "./components/TagEvidenceModal";
import GraphSelector from "../DataDiscovery/Graph/GraphSelector";
import { GraphContext } from "../../../../../contexts/GraphContext";
import FindReplacePanel from "./components/FindReplacePanel";
import {
  ViewMetadataProvider,
  useViewMetadata,
} from "../../../../../contexts/ViewMetadataContext";
import * as Sentry from "@sentry/react";
// import QueryBar from "./components/QueryBar";

// Hook for detecting clicks outside of a ref
function useOutsideAlerter(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

const MetadataTableInner = ({
  backendFilters,
  onFiltersChange,
  dataslice_id = null,
  compact = false,
  source = "viewMetadata",
  nodeData = null,
  selectedColumns: externalSelectedColumns = null,
  availableColumns: externalAvailableColumns = null,
  showAllColumns: externalShowAllColumns = null,
  vector_db_config = null,
  loadedOnce = false,
  conditionsNew = null,
  setConditionsNew = null,
}) => {
  const {
    deasyUserConfig,
    isConfigRefreshing,
    dataSlices,
    setCurrentScreen,
    vdbFilesCount,
    savedTags,
    vdbLastActiveProfileName,
  } = useContext(BaseContext);
  const { selectedDiscoveryGraph } = useContext(GraphContext);
  const {
    globalDistributions,
    filteredDistributions,
    loadingDistributions,
    updateDistributions,
    findReplacePanel,
    openFindReplace,
    closeFindReplace,
  } = useViewMetadata();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);
  const [loadingValues] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [chunkData, setChunkData] = useState({});
  const [loadingChunks, setLoadingChunks] = useState({});
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initialColumns = [deasyFileKey];
    Object.keys(backendFilters).forEach((column) => {
      if (!initialColumns.includes(column)) {
        initialColumns.push(column);
      }
    });
    return initialColumns;
  });
  const [availableColumns, setAvailableColumns] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    controls: false,
    filters: false,
    help: false,
  });
  const [distributionLimit] = useState(10);
  const [showDistributions] = useState(true);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);
  const [activeEvidence, setActiveEvidence] = useState(null);
  const [showEvidence, setShowEvidence] = useState(false);
  const [activeFullText, setActiveFullText] = useState(null);
  const [, setVisibleItemsCount] = useState({});
  const [evidenceRefs] = useState(() => new Map());
  const [loadingEvidenceMap, setLoadingEvidenceMap] = useState({});
  const [selectedDataSlice, setSelectedDataSlice] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [inputSearchQuery, setInputSearchQuery] = useState("");
  const debouncedExpandedSections = expandedSections;
  const [activeStandardizeColumn, setActiveStandardizeColumn] = useState(null);
  const [, setStandardizeTagStats] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [deleteStats, setDeleteStats] = useState({});
  const [isDeletionStatsLoading, setIsDeletionStatsLoading] = useState(false);
  const [tagEvidence, setTagEvidence] = useState({
    isOpen: false,
    tagName: null,
    value: null,
    fileName: null,
  });

  // const [queryDialogOpen, setQueryDialogOpen] = useState(true);

  // Add state for dynamic popup styles
  const [columnPopupStyle, setColumnPopupStyle] = useState({});
  const [filterPopupStyle, setFilterPopupStyle] = useState({});

  // Add refs for positioning the popups
  const filterButtonRef = useRef(null);
  const columnsButtonRef = useRef(null);
  const filterPopupRef = useRef(null);
  const columnPopupRef = useRef(null);

  // Add a ref to track if the component is mounted
  const isMounted = useRef(true);

  // Add handlers to close popups on outside click
  useOutsideAlerter(filterPopupRef, () => {
    if (expandedSections.filters) {
      handleSectionToggle("filters");
    }
  });

  useOutsideAlerter(columnPopupRef, () => {
    if (expandedSections.controls) {
      handleSectionToggle("controls");
    }
  });

  // If external props are provided, use them instead of internal state.
  const effectiveAvailableColumns =
    externalAvailableColumns || availableColumns;
  const effectiveShowAllColumns =
    externalShowAllColumns !== null ? externalShowAllColumns : showAllColumns;

  const handleSectionToggle = React.useCallback((section) => {
    setExpandedSections((prev) => {
      const newState = { ...prev };

      // If opening the controls section (Add columns), close the filters section
      if (section === "controls" && !prev[section]) {
        newState.filters = false;
      }

      newState[section] = !prev[section];
      return newState;
    });
  }, []);

  const handleColumnSelection = React.useCallback(
    (columns) => {
      // If columns is empty array (deselect all case), only keep deasyFileKey
      if (columns.length === 0) {
        setSelectedColumns([deasyFileKey]);
        setShowAllColumns(false);
        return;
      }

      // Ensure deasyFileKey is always included
      if (!columns.includes(deasyFileKey)) {
        columns.unshift(deasyFileKey);
      }

      setSelectedColumns(columns);
      // Update showAllColumns based on whether all columns are selected
      setShowAllColumns(
        columns.length === effectiveAvailableColumns.length + 1,
      ); // +1 for deasyFileKey
    },
    [effectiveAvailableColumns],
  );

  const handleSelectAll = React.useCallback(() => {
    const allColumns = [deasyFileKey, ...effectiveAvailableColumns];
    setSelectedColumns(allColumns);
    setShowAllColumns(true);
  }, [effectiveAvailableColumns]);

  const handleDeselectAll = React.useCallback(() => {
    setSelectedColumns([deasyFileKey]);
    setShowAllColumns(false);
  }, []);

  const handleDeleteTags = useCallback(
    async (tags) => {
      // Close the column selector popup before opening delete modal
      setExpandedSections((prev) => ({
        ...prev,
        controls: false,
      }));

      // Show modal immediately with loading state
      setTagsToDelete(tags);
      setDeleteStats({});
      setIsDeletionStatsLoading(true);
      setShowDeleteModal(true);

      try {
        const statsPromises = tags.map(async (tag) => {
          try {
            const result = await tagService.getTagDeleteStats(
              deasyUserConfig.deasyApiKey,
              tag,
            );
            return { tag, data: result.data, error: null };
          } catch (error) {
            return { tag, data: null, error: error };
          }
        });
        const statsResults = await Promise.all(statsPromises);
        const failedRequests = statsResults.filter((result) => result.error);
        if (failedRequests.length > 0) {
          throw new Error(
            `Failed to fetch stats for tags: ${failedRequests.map((r) => r.tag).join(", ")}`,
          );
        }
        const combinedStats = statsResults.reduce((acc, { tag, data }) => {
          acc[tag] = data;
          return acc;
        }, {});

        setDeleteStats(combinedStats);
      } catch (error) {
        console.error("Error fetching tag stats:", error);
        toast.error("Failed to prepare tag deletion. Please try again.");
        // Close modal on error
        setShowDeleteModal(false);
      } finally {
        setIsDeletionStatsLoading(false);
      }
    },
    [deasyUserConfig.deasyApiKey],
  );

  const columnSelectorProps = React.useMemo(
    () => ({
      availableColumns,
      selectedColumns,
      setSelectedColumns: handleColumnSelection,
      showAllColumns,
      setShowAllColumns: (show) => {
        if (show) {
          handleSelectAll();
        } else {
          handleDeselectAll();
        }
      },
      activeFilters: backendFilters,
      distributions: filteredDistributions,
      globalDistributions: globalDistributions,
      key: refreshKey,
      onDeleteTags: handleDeleteTags,
      dataslice_id,
      node_condition: nodeData ? nodePathToConditions(nodeData.nodePath) : null,
      totalRows,
    }),
    [
      availableColumns,
      selectedColumns,
      showAllColumns,
      handleColumnSelection,
      handleSelectAll,
      handleDeselectAll,
      backendFilters,
      filteredDistributions,
      globalDistributions,
      refreshKey,
      handleDeleteTags,
      dataslice_id,
      nodeData,
      totalRows,
    ],
  );

  const displayColumns = useMemo(() => {
    // If external columns are provided, use those
    if (externalSelectedColumns) {
      return externalSelectedColumns;
    }

    // Otherwise use selected columns, ensuring deasyFileKey is always first
    const columns = selectedColumns.includes(deasyFileKey)
      ? selectedColumns
      : [deasyFileKey, ...selectedColumns];

    // If showAllColumns is true, show all available columns
    if (effectiveShowAllColumns) {
      return [deasyFileKey, ...effectiveAvailableColumns];
    }

    return columns;
  }, [
    externalSelectedColumns,
    selectedColumns,
    effectiveShowAllColumns,
    effectiveAvailableColumns,
  ]);

  const handleRemoveFilter = (column, value) => {
    // Update the flat filter structure
    onFiltersChange((prev) => {
      const newFilters = { ...prev };
      if (newFilters[column]) {
        // If the value is a string, it's a simple filter
        if (typeof value === "string") {
          newFilters[column] = newFilters[column].filter((v) => {
            // Handle both string values and object values with operators
            if (typeof v === "string") {
              return v !== value;
            } else if (v && typeof v === "object") {
              return v.value !== value;
            }
            return true;
          });
        }
        // If the value is an object with operator info, filter by the value and operator
        else if (value && typeof value === "object" && value.value) {
          newFilters[column] = newFilters[column].filter((v) => {
            if (typeof v === "string") {
              return true; // Keep string values
            } else if (v && typeof v === "object") {
              // Match both value and operator for removal
              return !(
                v.value === value.value && v.operator === value.operator
              );
            }
            return true;
          });
        }

        if (newFilters[column].length === 0) {
          delete newFilters[column];
        }
      }
      return newFilters;
    });

    // Update the conditionals_new structure
    setConditionsNew((prev) => {
      if (!prev) return null;

      // Create a deep copy to avoid mutating the original state
      const updatedStructure = JSON.parse(JSON.stringify(prev));

      // Flag to check if any changes were made
      let changesMade = false;

      // Helper function to find and remove the tag/value
      const removeTagFromGroups = () => {
        // Go through each group
        for (let i = 0; i < updatedStructure.children.length; i++) {
          const group = updatedStructure.children[i];
          if (!group.children) continue;

          // Find the tag in the group's children
          const initialLength = group.children.length;
          group.children = group.children.filter((child) => {
            if (!child.tag) return true;
            if (child.tag.name !== column) return true;

            // Check if value matches
            if (typeof value === "string") {
              return !child.tag.values.includes(value);
            } else if (typeof value === "object" && value.value) {
              return !(
                child.tag.values.includes(value.value) &&
                child.tag.operator === value.operator
              );
            }
            return true;
          });

          // Check if any items were removed
          if (group.children.length < initialLength) {
            changesMade = true;

            // If group is empty, remove it
            if (group.children.length === 0) {
              updatedStructure.children.splice(i, 1);
              i--; // Adjust the loop counter
            }
          }
        }
      };

      removeTagFromGroups();

      // If no changes were made or no groups left, keep the original structure
      if (!changesMade) {
        return prev;
      }

      // If no groups left, return null
      if (updatedStructure.children.length === 0) {
        return null;
      }

      return updatedStructure;
    });

    // Refresh, but preserve data slice selection
    setRefreshKey((prev) => prev + 1);
    setPage(0);
  };

  const handleClearFilters = () => {
    onFiltersChange({});
    setPage(0);
    setConditionsNew(null);
    setRefreshKey((prev) => prev + 1);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    // Clear all filters
    onFiltersChange({});
    // Reset data slice selection
    setSelectedDataSlice(null);
    // Reset page
    setPage(0);
    // Reset expanded sections
    setExpandedSections((prev) => ({
      ...prev,
      filters: false,
    }));
    // Refresh data
    setRefreshKey((prev) => prev + 1);
  };

  const handleDistributionClick = async (column, value) => {
    try {
      setLoading(true);

      const newFilters = { ...backendFilters };
      if (!newFilters[column]) {
        newFilters[column] = [];
      }

      const valueIndex = newFilters[column].findIndex((v) => {
        if (typeof v === "string") return v === value;
        if (typeof v === "object" && v.value) return v.value === value;
        return false;
      });

      if (valueIndex === -1) {
        // Adding a filter
        newFilters[column] = [...newFilters[column], value];
        // Auto-select column on filter add
        if (!selectedColumns.includes(column)) {
          setSelectedColumns((prev) => [...prev, column]);
        }
      } else {
        // Removing a filter
        newFilters[column] = [
          ...newFilters[column].slice(0, valueIndex),
          ...newFilters[column].slice(valueIndex + 1),
        ];

        if (newFilters[column].length === 0) {
          delete newFilters[column];
        }

        // Update the conditionals_new structure when removing a filter
        setConditionsNew((prev) => {
          if (!prev) return null;

          // Helper function to recursively remove a tag/value from the structure
          const removeTagValueFromStructure = (node) => {
            if (!node) return null;

            // If this is a tag node with the value to remove
            if (
              node.tag &&
              node.tag.name === column &&
              node.tag.values.includes(value)
            ) {
              return null; // Remove this node
            }

            // If this is a group node, process its children
            if (node.children) {
              // Process children and filter out nulls
              const newChildren = node.children
                .map(removeTagValueFromStructure)
                .filter(Boolean);

              // If no children left, return null
              if (newChildren.length === 0) return null;

              // Otherwise return updated node
              return {
                ...node,
                children: newChildren,
              };
            }
            return node;
          };

          // Process the entire structure
          const updatedStructure = removeTagValueFromStructure({ ...prev });

          // If root is null or has no children, return null
          if (
            !updatedStructure ||
            !updatedStructure.children ||
            updatedStructure.children.length === 0
          ) {
            return null;
          }
          return updatedStructure;
        });
      }

      onFiltersChange(newFilters);
      setPage(0);
    } catch (error) {
      console.error("Error applying distribution filter:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDistributionExpansion = (column, direction = "more") => {
    setVisibleItemsCount((prev) => {
      const currentCount = prev[column] || distributionLimit;

      if (direction === "more") {
        // Simply add 10 more without checking total
        return { ...prev, [column]: currentCount + 10 };
      } else {
        // Reduce by 10, but don't go below minimum
        return {
          ...prev,
          [column]: Math.max(currentCount - 10, distributionLimit),
        };
      }
    });
  };

  useEffect(() => {
    if (!conditionsNew && Object.keys(backendFilters).length > 0) {
      // Create default structure with OR between all filters
      const defaultStructure = {
        condition: "OR", // Default to OR between groups
        children: [],
      };
      const defaultConditionGroup = {
        condition: "AND",
        children: [],
      };

      // Add each filter tag as a separate child initially
      Object.entries(backendFilters).forEach(([column, values]) => {
        values.forEach((value) => {
          // Check if the value has an operator
          if (typeof value === "object" && value.value && value.operator) {
            defaultConditionGroup.children.push({
              tag: {
                name: column,
                values: [value.value],
                operator: value.operator,
              },
            });
          } else {
            defaultConditionGroup.children.push({
              tag: {
                name: column,
                values: [value],
              },
            });
          }
        });
        defaultStructure.children.push(defaultConditionGroup);
      });

      setConditionsNew(defaultStructure);
    } else if (conditionsNew && Object.keys(backendFilters).length > 0) {
      // Check if all filters are included in conditionals_new
      // This ensures all filters always show up in the Rules Builder
      let allFiltersIncluded = true;
      let missingFilters = [];

      // Flatten the conditionals_new structure to find all included tags
      const flattenStructure = (node, flatTags = []) => {
        if (node.tag) {
          // Include operator if available in the key
          const operator = node.tag.operator ? `${node.tag.operator}:` : "";
          flatTags.push(`${node.tag.name}:${operator}${node.tag.values[0]}`);
        }
        if (node.children && Array.isArray(node.children)) {
          node.children.forEach((child) => flattenStructure(child, flatTags));
        }
        return flatTags;
      };

      const includedTags = flattenStructure(conditionsNew);

      // Check if any filter is missing
      Object.entries(backendFilters).forEach(([column, values]) => {
        values.forEach((value) => {
          // For values with operators
          if (typeof value === "object" && value.value && value.operator) {
            const tagKey = `${column}:${value.operator}:${value.value}`;
            if (!includedTags.includes(tagKey)) {
              allFiltersIncluded = false;
              missingFilters.push({
                name: column,
                values: [value.value],
                operator: value.operator,
              });
            }
          } else {
            // For simple string values
            const tagKey = `${column}:${value}`;
            if (!includedTags.includes(tagKey)) {
              allFiltersIncluded = false;
              missingFilters.push({ name: column, values: [value] });
            }
          }
        });
      });

      if (!allFiltersIncluded) {
        setConditionsNew((prev) => {
          const newStructure = JSON.parse(JSON.stringify(prev));
          const lastGroupIndex = newStructure.children.length - 1;

          missingFilters.forEach((filter) => {
            if (lastGroupIndex >= 0) {
              newStructure.children[lastGroupIndex].children.push({
                tag: filter,
              });
            } else {
              newStructure.children.push({
                condition: "AND",
                children: [{ tag: filter }],
              });
            }
          });
          return newStructure;
        });
      }
    }
  }, [backendFilters, conditionsNew, setConditionsNew]);

  const renderDistributionChart = (column) => {
    if (!showDistributions) return null;

    if (loadingDistributions) {
      return (
        <div className="h-full w-full flex items-center justify-center">
          <CircularProgress size={20} sx={{ color: "#9ca3af" }} />
        </div>
      );
    }

    const processedCurrentDistributions = filteredDistributions[column] || {};
    const globalColumnDistributions = globalDistributions[column] || {};

    if (
      (!processedCurrentDistributions ||
        Object.keys(processedCurrentDistributions).length === 0) &&
      (!globalColumnDistributions ||
        Object.keys(globalColumnDistributions).length === 0)
    ) {
      return (
        <div className="h-full w-full flex items-center justify-center text-gray-400 text-xs">
          No data
        </div>
      );
    }

    return (
      <DistributionChart
        column={column}
        currentDistributions={processedCurrentDistributions}
        globalColumnDistributions={globalColumnDistributions}
        backendFilters={backendFilters}
        distributionLimit={distributionLimit}
        compactMode={compact}
        onDistributionClick={handleDistributionClick}
        onToggleExpansion={toggleDistributionExpansion}
      />
    );
  };

  const toggleChunkText = (chunkId) => {
    setActiveFullText((prev) => (prev === chunkId ? null : chunkId));
  };

  const CreateDataSliceButton = () => {
    // Only render if filters are applied
    if (Object.keys(backendFilters).length === 0) return null;

    return (
      // Apply styling to match other toolbar buttons
      <button
        onClick={() => setShowCreateDataSlice(true)}
        className="flex items-center gap-1.5 text-sm px-2 py-1 rounded-md border border-gray-200 hover:border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
        title="Create Data Slice from Filters"
      >
        {/* Use FaLayerGroup icon */}
        <FaLayerGroup size={11} />
        <span className="text-xs">Create Slice</span>
      </button>
    );
  };

  const handleFetchChunkEvidence = (fileName, chunkId, tagName, value) => {
    fetchChunkEvidence({
      fileName,
      chunkId,
      tagName,
      value,
      chunkData,
      deasyUserConfig,
      setChunkData,
      setActiveEvidence,
      setLoadingEvidenceMap,
    });
  };

  const renderEvidenceRow = (chunk, chunkId, row) => (
    <EvidenceRow
      chunk={chunk}
      chunkId={chunkId}
      activeEvidence={activeEvidence}
      activeFullText={activeFullText}
      evidenceRefs={evidenceRefs}
      toggleChunkText={toggleChunkText}
      TABLE_COLUMN_WIDTHS={TABLE_COLUMN_WIDTHS}
    />
  );

  const handleSelectDataSliceChange = (selected) => {
    handleDataSliceChange({
      selected,
      onFiltersChange,
      setAvailableColumns,
      setSelectedColumns,
      setSelectedDataSlice,
      deasyFileKey,
      setConditionsNew,
      setRefreshKey,
    });
  };

  // Add an effect to handle closing the Active Filters section when no filters exist
  useEffect(() => {
    if (Object.keys(backendFilters).length === 0) {
      setExpandedSections((prev) => ({
        ...prev,
        filters: false,
      }));
    }
  }, [backendFilters]);

  // Add useEffect for handling click outside evidence chunks
  useEffect(() => {
    if (!activeEvidence) return;

    const handleClickOutside = (event) => {
      // Check if click is outside any evidence chunk
      const isClickInEvidence = Array.from(evidenceRefs.values()).some(
        (ref) => ref && ref.contains(event.target),
      );

      if (!isClickInEvidence) {
        setActiveEvidence(null);
        setActiveFullText(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeEvidence, evidenceRefs]);

  // Add this function to handle standardize panel state
  const handleStandardizePanelState = (column, stats) => {
    setActiveStandardizeColumn(column);
    setStandardizeTagStats(stats);
  };

  const handleTagClick = (tagName, value, fileName) => {
    setTagEvidence({
      isOpen: true,
      tagName,
      value,
      fileName,
    });
  };

  const handleCloseTagEvidence = () => {
    setTagEvidence({
      isOpen: false,
      tagName: null,
      value: null,
      fileName: null,
    });
  };

  // Add this function to handle the "Add Schema Columns" button click
  const handleAddSchemaColumns = () => {
    if (!selectedDiscoveryGraph || selectedDiscoveryGraph.graph_id === null) {
      toast.error("Please select a metadata schema first");
      return;
    }

    const schemaTags = extractTagsFromSchema(selectedDiscoveryGraph.graph_data);

    // Find intersection between schema tags and available columns
    const columnsToAdd = schemaTags.filter((tag) =>
      availableColumns.includes(tag),
    );

    if (columnsToAdd.length === 0) {
      toast("Schema has not been tagged with selected data connector");
      return;
    }
    // Add the schema tags to selected columns
    setSelectedColumns(columnsToAdd);
    toast.success(
      `Added ${columnsToAdd.length} columns from schema "${selectedDiscoveryGraph.graph_name}"`,
    );
  };

  // Calculate applicable schema tags count
  const applicableSchemaTagCount = useMemo(() => {
    if (!selectedDiscoveryGraph?.graph_data || !availableColumns?.length) {
      return 0;
    }
    try {
      const schemaTags = extractTagsFromSchema(
        selectedDiscoveryGraph.graph_data,
      );
      // Find intersection between schema tags and available columns
      const count = schemaTags.filter((tag) =>
        availableColumns.includes(tag),
      ).length;
      return count;
    } catch (error) {
      console.error("Error extracting tags from schema:", error);
      return 0;
    }
  }, [selectedDiscoveryGraph, availableColumns]);

  useEffect(
    () => {
      const fetchDistributionsAndMetadata = async () => {
        if (!deasyUserConfig?.deasyApiKey) {
          return;
        }

        try {
          updateDistributions(globalDistributions, filteredDistributions, true);
          setLoading(true);

          const nodeConditions = nodeData
            ? nodePathToConditions(nodeData.nodePath)
            : null;

          const getPaginatedDistributions = async () => {
            // Note: Limit is the number of values per tag, not the total number of values
            let maxValPerTag = 500;
            // By maxValPerTag, we mean the number of values per tag (truncating on the backend)

            const response = await metadataService.getDistributions(
              vdbLastActiveProfileName,
              deasyUserConfig.deasyApiKey,
              dataslice_id,
              nodeConditions,
              maxValPerTag,
              conditionsNew,
              selectedColumns.filter(
                (column) => !column.includes(deasyFileKey),
              ),
            );
            return response.data.distributions;
          };
          // Fetch all data in parallel with dataslice_id

          console.log("Fetching metadata with conditionsNew:", conditionsNew);

          const [globalResponse, filteredMetadataResponse, uniqueTagsResponse] =
            await Promise.all([
              getPaginatedDistributions(),
              metadataService.getFilteredMetadata(
                vdbLastActiveProfileName,
                page * rowsPerPage,
                rowsPerPage,
                deasyUserConfig.deasyApiKey,
                dataslice_id,
                nodeConditions,
                searchQuery,
                conditionsNew,
              ),
              metadataService.getUniqueTags(
                deasyUserConfig.deasyApiKey,
                null,
                vdbLastActiveProfileName,
                dataslice_id,
                nodeConditions,
              ),
            ]);

          if (uniqueTagsResponse?.data?.tags) {
            const formattedTags = uniqueTagsResponse.data.tags;
            setAvailableColumns(formattedTags);
          }
          if (globalResponse && Object.keys(globalResponse).length === 0) {
            // Don't return early, just set distribution-related states
            if (!compact) {
              setTotalRows(0);
              setHasMore(false);
              updateDistributions({}, {}, false);
              // We'll continue to process metadata below
            }
          }

          if (globalResponse) {
            updateDistributions(globalResponse, globalResponse, false);
          } else {
            // Ensure we set empty distributions even if globalResponse is null or undefined
            updateDistributions({}, {}, false);
          }
          if (
            filteredMetadataResponse?.data?.metadata &&
            Object.keys(filteredMetadataResponse?.data?.metadata).length > 0
          ) {
            const transformedData = Object.entries(
              filteredMetadataResponse.data.metadata,
            ).map(([fileName, metadata]) => ({
              [deasyFileKey]: fileName,
              ...metadata,
              chunks: [],
            }));

            // Ensure we set the table data with a small delay to avoid race conditions
            setTimeout(() => {
              if (isMounted.current) {
                setTableData(transformedData);
                setTotalRows(filteredMetadataResponse?.data?.total_matches);
                setHasMore(
                  filteredMetadataResponse?.data?.next_offset !== null,
                );
              }
            }, 0);

            // Calculate filtered distributions
            updateDistributions(globalDistributions, globalResponse, false);
          } else {
            // Handle empty metadata response by setting empty table data
            setTableData([]);
            setTotalRows(0);
            setHasMore(false);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          Sentry.captureException(error);
        } finally {
          setLoading(false);
          // Loading state is handled in updateDistributions calls above
        }
      };

      fetchDistributionsAndMetadata();
    },
    // eslint-disable-next-line
    [
      deasyUserConfig,
      page,
      searchQuery,
      rowsPerPage,
      refreshKey,
      dataslice_id,
      compact,
      nodeData,
      vector_db_config,
      conditionsNew,
      selectedColumns,
      selectedDiscoveryGraph,
    ],
  );

  const handleFilterClick = async (event, column) => {
    // Prevent opening filter menu for file key
    if (column === deasyFileKey) return;
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
    // Potentially fetch available values for the column here if needed by FilterMenu
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const handleFilterSelect = async (valueOrObject) => {
    if (!activeColumn) return;

    // Determine the value and operator to add
    let valueToAdd;
    let operator = OPERATORS.ANY; // Default operator

    if (
      typeof valueOrObject === "object" &&
      valueOrObject.value !== undefined
    ) {
      valueToAdd = valueOrObject.value;
      operator = valueOrObject.operator || operator; // Use provided operator or default
    } else {
      valueToAdd = valueOrObject;
    }

    // Add the new filter to the flat structure while preserving data slice filters
    onFiltersChange((prev) => {
      const newFilters = { ...prev };
      if (!newFilters[activeColumn]) {
        newFilters[activeColumn] = [];
      }

      // Check if the exact filter (value + operator) already exists
      const filterExists = newFilters[activeColumn].some((existingFilter) => {
        if (
          typeof existingFilter === "object" &&
          existingFilter.value !== undefined
        ) {
          return (
            existingFilter.value === valueToAdd &&
            existingFilter.operator === operator
          );
        }
        // If it's a simple value filter being added with ANY operator
        return existingFilter === valueToAdd && operator === OPERATORS.ANY;
      });

      if (!filterExists) {
        // Use object format only if operator is not ANY
        const filterObject =
          operator === OPERATORS.ANY
            ? valueToAdd
            : { value: valueToAdd, operator };
        newFilters[activeColumn] = [...newFilters[activeColumn], filterObject];

        // Auto-select column on filter add
        if (!selectedColumns.includes(activeColumn)) {
          setSelectedColumns((prev) => [...prev, activeColumn]);
        }
      }

      return newFilters;
    });

    // Add the new filter to the conditional structure
    setConditionsNew((prev) => {
      if (!prev) {
        // Create a new structure if none exists
        return {
          condition: "AND",
          children: [
            {
              condition: "AND",
              children: [
                {
                  tag: {
                    name: activeColumn,
                    values: [valueToAdd],
                    operator: operator !== OPERATORS.ANY ? operator : undefined,
                  },
                },
              ],
            },
          ],
        };
      }

      // Create a deep copy to avoid mutating the original state
      const updatedStructure = JSON.parse(JSON.stringify(prev));

      // Try to find an existing group to add the new filter to
      let targetGroup = null;
      if (updatedStructure.children && updatedStructure.children.length > 0) {
        // Try to find a group with less than 5 children to add to
        targetGroup = updatedStructure.children.find(
          (group) => group.children && group.children.length < 5,
        );

        // If no suitable group found, add to the first group
        if (!targetGroup && updatedStructure.children.length > 0) {
          targetGroup = updatedStructure.children[0];
        }
      }

      // If no target group found, create a new one
      if (!targetGroup) {
        targetGroup = {
          condition: "AND",
          children: [],
        };
        updatedStructure.children.push(targetGroup);
      }

      // Add the new filter to the target group
      targetGroup.children.push({
        tag: {
          name: activeColumn,
          values: [valueToAdd],
          operator: operator !== OPERATORS.ANY ? operator : undefined,
        },
      });

      return updatedStructure;
    });

    // Refresh the data without modifying the data slice selection
    setRefreshKey((prev) => prev + 1);
    setPage(0);
  };

  // New handler for adding filters by clicking table cells
  const handleAddFilterFromCell = (column, value) => {
    if (!column || value === null || value === undefined) return;

    // Add the new filter to the flat structure
    onFiltersChange((prev) => {
      const newFilters = { ...prev };
      if (!newFilters[column]) {
        newFilters[column] = [];
      }
      // Only add if the value doesn't exist
      if (!newFilters[column].includes(value)) {
        newFilters[column] = [...newFilters[column], value];

        // Auto-select column on filter add
        if (!selectedColumns.includes(column)) {
          setSelectedColumns((prev) => [...prev, column]);
        }
      }
      return newFilters;
    });

    // Add the new filter to the conditional structure
    setConditionsNew((prev) => {
      if (!prev) return null;

      // Helper function to recursively add a tag/value to the structure
      const addTagValueToStructure = (node) => {
        if (!node) return null;

        // If this is a tag node with the value to add
        if (node.tag && node.tag.name === column) {
          // For normal string values
          if (typeof value === "string" && node.tag.values.includes(value)) {
            return node;
          }
          // For object values with operators
          else if (value && typeof value === "object" && value.value) {
            const matchesOperator = node.tag.operator === value.operator;
            const matchesValue = node.tag.values.includes(value.value);

            if (matchesOperator && matchesValue) {
              return node;
            }
          }
        }

        // If this is a group node, process its children
        if (node.children) {
          // Process children and filter out nulls
          const newChildren = node.children
            .map(addTagValueToStructure)
            .filter(Boolean);

          // If no children left, return null
          if (newChildren.length === 0) return null;

          // Otherwise return updated node
          return {
            ...node,
            children: newChildren,
          };
        }
        return node;
      };

      // Process the entire structure
      const updatedStructure = addTagValueToStructure({ ...prev });

      // If root is null or has no children, return null
      if (
        !updatedStructure ||
        !updatedStructure.children ||
        updatedStructure.children.length === 0
      ) {
        return null;
      }
      return updatedStructure;
    });

    setRefreshKey((prev) => prev + 1);
    setPage(0);
  };

  // Effect to calculate Column Selector position
  useEffect(() => {
    if (debouncedExpandedSections.controls && columnsButtonRef.current) {
      const buttonRect = columnsButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupMinWidth = 400; // Or read from style/constants
      const popupMaxHeight = viewportHeight * 0.7; // e.g., max 70% of viewport height
      const padding = 16; // Generous padding from viewport edges

      let top = buttonRect.bottom + 8;
      let left = "auto";
      let right = "auto";
      let maxHeight = popupMaxHeight;
      const maxWidth = Math.min(
        viewportWidth * 0.4,
        viewportWidth - 2 * padding,
      ); // e.g., max 40vw or viewport width minus padding

      // Check vertical overflow (bottom)
      if (top + maxHeight > viewportHeight - padding) {
        // Try positioning above the button
        const topAbove = buttonRect.top - 8 - maxHeight;
        if (topAbove > padding) {
          // Check if there's space above
          top = topAbove;
        } else {
          // Not enough space above either, stick to bottom with max height
          top = viewportHeight - padding - maxHeight;
        }
      }
      // Ensure top doesn't go negative
      top = Math.max(padding, top);

      // Horizontal positioning (try aligning right edge first)
      const idealRight = viewportWidth - buttonRect.right;
      // Estimate popup width (use minWidth for calculation, maxWidth constrains)
      const estimatedWidth = Math.min(
        maxWidth,
        Math.max(popupMinWidth, buttonRect.width),
      );

      if (buttonRect.right - estimatedWidth < padding) {
        // Check if aligning right pushes off left
        // Align left instead
        left = `${padding}px`;
        right = "auto";
      } else {
        // Align right
        right = `${Math.max(padding, idealRight)}px`;
        left = "auto";
      }

      setColumnPopupStyle({
        position: "fixed", // Use fixed to position relative to viewport
        top: `${top}px`,
        left: left,
        right: right,
        minWidth: `${popupMinWidth}px`,
        maxWidth: `${maxWidth}px`,
        maxHeight: `${maxHeight}px`,
        width: "max-content", // Let content determine width up to max
      });
    }
  }, [debouncedExpandedSections.controls]);

  // Effect to calculate Filter Logic position
  useEffect(() => {
    if (debouncedExpandedSections.filters && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupMinWidth = 400; // Or read from style/constants
      const popupMaxHeight = viewportHeight * 0.7; // e.g., max 70% of viewport height
      const padding = 16; // Generous padding from viewport edges

      let top = buttonRect.bottom + 8;
      let left = "auto";
      let right = "auto";
      let maxHeight = popupMaxHeight;
      const maxWidth = Math.min(
        viewportWidth * 0.4,
        viewportWidth - 2 * padding,
      ); // e.g., max 40vw or viewport width minus padding

      // Check vertical overflow (bottom)
      if (top + maxHeight > viewportHeight - padding) {
        // Try positioning above the button
        const topAbove = buttonRect.top - 8 - maxHeight;
        if (topAbove > padding) {
          // Check if there's space above
          top = topAbove;
        } else {
          // Not enough space above either, stick to bottom with max height
          top = viewportHeight - padding - maxHeight;
        }
      }
      // Ensure top doesn't go negative
      top = Math.max(padding, top);

      // Horizontal positioning (try aligning left edge first)
      const idealLeft = buttonRect.left;
      // Estimate popup width
      const estimatedWidth = Math.min(
        maxWidth,
        Math.max(popupMinWidth, buttonRect.width),
      );

      if (buttonRect.left + estimatedWidth > viewportWidth - padding) {
        // Check if aligning left pushes off right
        // Align right instead
        right = `${padding}px`;
        left = "auto";
      } else {
        // Align left
        left = `${Math.max(padding, idealLeft)}px`;
        right = "auto";
      }

      setFilterPopupStyle({
        position: "fixed", // Use fixed to position relative to viewport
        top: `${top}px`,
        left: left,
        right: right,
        minWidth: `${popupMinWidth}px`,
        maxWidth: `${maxWidth}px`,
        maxHeight: `${maxHeight}px`,
        width: "max-content", // Let content determine width up to max
      });
    }
  }, [debouncedExpandedSections.filters]);

  if (tableData.length === 0 && !compact && !loading) {
    if (Object.keys(backendFilters).length === 0) {
      return (
        <BlankMetadataScreen
          onStartExtraction={() =>
            setCurrentScreen(WorkflowType.EXTRACT_METADATA)
          }
        />
      );
    }
  }

  return (
    <div className="flex flex-col w-full relative">
      {/* Loading modal */}
      {isConfigRefreshing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Modal */}
          <div className="relative bg-[#1a1f2b] rounded-xl shadow-2xl p-8 m-4 max-w-sm w-full">
            <div className="flex flex-col items-center gap-6">
              <div className="relative">
                <img
                  src="/deasy_shrimp.png"
                  alt="Deasy Labs"
                  className="h-16 w-auto animate-pulse"
                />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-white mb-2">
                  Updating Data
                </p>
                <p className="text-sm text-gray-50">
                  Please wait while we refresh your metadata...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!compact && (
        <>
          {/* Notion-like integrated toolbar - Now more compact */}
          <div className="flex items-center w-full gap-2 mb-4 bg-white rounded-lg border border-gray-200 p-1.5 relative z-10">
            {/* === MOVE CompactDataSliceSelector HERE === */}
            {dataSlices && dataSlices.length > 0 && (
              <div className="flex items-center">
                <CompactDataSliceSelector
                  selectedDataSlice={selectedDataSlice}
                  onSelect={handleSelectDataSliceChange}
                  dataSlices={dataSlices}
                  createNewLabel="Slice All Files"
                  deasyUserConfig={deasyUserConfig}
                  vdbFilesCount={vdbFilesCount}
                />
              </div>
            )}
            {/* === END MOVE === */}

            {/* Schema Selector */}
            <div className="flex items-center gap-2 border-l border-gray-200 pl-2 ml-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Schema:
              </span>
              <GraphSelector
                viewOnly={true}
                verbose={false}
                filterGraphs={(graph) => {
                  if (!graph || !graph.graph_data) return false;

                  // Extract tags from this graph
                  const schemaTags = extractTagsFromSchema(graph.graph_data);

                  // Check if any tags from this schema are available in the data
                  return schemaTags.some((tag) =>
                    availableColumns.includes(tag),
                  );
                }}
              />
              {/* Only show button when there are applicable schema tags */}
              {selectedDiscoveryGraph &&
                selectedDiscoveryGraph.graph_id !== null &&
                applicableSchemaTagCount > 0 && (
                  <button
                    onClick={handleAddSchemaColumns}
                    className={`px-2 py-1 text-xs text-left rounded-md bg-indigo-50 text-gray hover:bg-indigo-100 transition-colors whitespace-nowrap ${
                      loading
                        ? "animate-pulse bg-indigo-100"
                        : selectedDiscoveryGraph?.graph_name &&
                            extractTagsFromSchema(
                              selectedDiscoveryGraph.graph_data,
                            ).filter((col) => selectedColumns.includes(col))
                              .length <= 1
                          ? "animate-pulse bg-primary text-white font-bold"
                          : "bg-indigo-100"
                    }`}
                    title={`Add all available columns from schema: ${selectedDiscoveryGraph?.graph_name}`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Show Metadata"}
                  </button>
                )}
            </div>

            {/* Filters display area - Ensure it's properly closed */}
            <div className="flex items-center flex-wrap flex-grow gap-1 min-h-[32px] border-l border-gray-200 pl-2 ml-2">
              {Object.keys(backendFilters).length > 0 ? (
                <>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs font-medium text-gray-500 mr-1">
                      Filters:
                    </span>
                    <div className="filter-chips flex flex-wrap">
                      {Object.entries(backendFilters).map(([column, values]) =>
                        values.map((value) => {
                          const hasOperator =
                            typeof value === "object" &&
                            value.value !== undefined &&
                            value.operator;
                          const displayValue = hasOperator
                            ? value.value
                            : value;
                          const operator = hasOperator ? value.operator : null;

                          return (
                            <div
                              key={`${column}-${hasOperator ? `${operator}-${displayValue}` : displayValue}`}
                              className="inline-flex items-center mr-1 mb-0 px-1.5 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs"
                            >
                              <span className="mr-1 font-medium text-gray-700">
                                {column}:
                              </span>
                              {operator && (
                                <span className="mr-0.5 text-gray-600">
                                  {operator === "ge"
                                    ? "≥"
                                    : operator === "gt"
                                      ? ">"
                                      : operator === "le"
                                        ? "≤"
                                        : operator === "lt"
                                          ? "<"
                                          : ""}
                                </span>
                              )}
                              <span className="text-gray-700">
                                {displayValue}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFilter(column, value);
                                }}
                                className="ml-1 text-gray-400 hover:text-red-500 focus:outline-none"
                              >
                                <FaTimes size={9} />
                              </button>
                            </div>
                          );
                        }),
                      )}
                    </div>
                  </div>
                  {/* Clear All Button */}
                  <button
                    onClick={handleClearFilters}
                    className="text-xs text-gray-400 hover:text-red-500 ml-1 p-0.5 rounded hover:bg-gray-100"
                    title="Clear all filters"
                  >
                    <FaTimes size={10} />
                  </button>
                  {/* New Advanced Filter Logic Button - appears only when filters exist */}
                  <button
                    ref={filterButtonRef} // Re-use ref for positioning popup
                    onClick={() => handleSectionToggle("filters")}
                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 hover:border-gray-300 transition-colors ml-2"
                  >
                    {/* Optional: Add an icon like FaCog or similar */}
                    <span className="text-xs">Filter Logic</span>
                  </button>
                </>
              ) : (
                <span className="text-xs text-gray-400 italic pl-2">
                  Click column header filter icons to add filters
                </span>
              )}
            </div>

            {/* Control buttons area */}
            <div className="flex items-center gap-1 flex-shrink-0 border-l border-gray-200 pl-2 ml-2">
              {/* Place CreateDataSliceButton HERE */}
              {Object.keys(backendFilters).length > 0 && (
                <CreateDataSliceButton />
              )}
              {/* Main Columns Button */}
              <button
                ref={columnsButtonRef}
                onClick={() => handleSectionToggle("controls")}
                className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-md border border-primary/30 hover:bg-primary/90 transition-colors 
                           bg-primary text-white`}
              >
                <FaPlus size={11} />
                <span className="text-xs">
                  Add Metadata{" "}
                  {applicableSchemaTagCount > 0 &&
                  selectedDiscoveryGraph?.graph_id !== null
                    ? `(${applicableSchemaTagCount})`
                    : ""}
                </span>
              </button>

              {/* Refresh button */}
              <button
                onClick={handleRefresh}
                className={`flex items-center justify-center p-1 text-gray-500 hover:text-gray-700 bg-gray-50 rounded-md border border-gray-200 hover:border-gray-300 transition-colors ${
                  loading ? "animate-spin" : ""
                }`}
                title="Refresh data"
              >
                <FaSync size={12} />
              </button>
            </div>
          </div>

          {/* Column Selector Popup */}
          {debouncedExpandedSections.controls &&
            ReactDOM.createPortal(
              <div
                ref={columnPopupRef}
                className="fixed z-[1200] overflow-y-auto p-4 bg-white rounded-lg border border-gray-200 shadow-lg"
                style={columnPopupStyle}
                onClick={(e) => e.stopPropagation()}
              >
                <MetadataColumnSelector {...columnSelectorProps} />
              </div>,
              document.body,
            )}

          {/* Filter Management Popup */}
          {debouncedExpandedSections.filters &&
            ReactDOM.createPortal(
              <div
                ref={filterPopupRef}
                className="fixed z-[1200] overflow-y-auto p-4 bg-white rounded-lg border border-gray-200 shadow-lg"
                style={filterPopupStyle}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex justify-between items-start">
                    <div className="w-2/3 pr-4">
                      <ActiveFilters
                        filters={backendFilters}
                        onRemove={handleRemoveFilter}
                        conditionals_new={conditionsNew}
                        setConditionalsNew={setConditionsNew}
                        setRefreshKey={setRefreshKey}
                      />
                    </div>
                  </div>

                  {Object.keys(backendFilters).length > 0 && (
                    <button
                      onClick={() => {
                        handleClearFilters();
                        // Close the popup after clearing
                        handleSectionToggle("filters");
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                      <FaSync
                        className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}
                      />
                      <span>Clear All Filters</span>
                    </button>
                  )}
                </div>
              </div>,
              document.body,
            )}
        </>
      )}

      {/* Standardize Panel */}
      {activeStandardizeColumn && (
        <div className="mb-4 w-full">
          <StandardizeValuesPanel
            isOpen={!!activeStandardizeColumn}
            onClose={() => handleStandardizePanelState(null, null)}
            tagsDetails={(() => {
              const tagDetails = savedTags.find(
                (tag) => tag.name === activeStandardizeColumn,
              );
              if (!tagDetails) {
                toast.error(
                  "This tag needs to be defined in the Metadata Library before it can be standardized. Please add it to the Metadata Library first.",
                );
                handleStandardizePanelState(null, null);
                return null;
              }
              return tagDetails;
            })()}
          />
        </div>
      )}

      {/* Rest of the table components */}
      <Paper
        className="metadata-table-paper"
        sx={{
          boxShadow: "0 1px 1px rgba(0,0,0,0.01)",
          border: "2px solid #d1d5db",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: compact ? "auto" : "fit-content",
          overflowX: "auto",
          "&::WebkitScrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            overflow: "auto",
            position: "relative",
            maxHeight: compact ? "400px" : "calc(100vh - 100px)",
            "&::WebkitScrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "& .MuiTableCell-root": {
              textRendering: "optimizeLegibility",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            },
            "& .MuiTableCell-head": {
              backgroundColor: "#f9fafb",
              fontWeight: "600",
            },
            "& .filename-column": {
              fontWeight: "500",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "340px",
              letterSpacing: "0.01em",
              padding: "8px 16px",
            },
          }}
        >
          <Table
            stickyHeader
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #E5E7EB !important",
              },
              "& .expanded-content-cell": {
                borderBottom: "none !important",
              },
              "& .MuiTableCell-body .MuiTableCell-root": {
                borderBottom: "none !important",
              },
              "& .MuiTableRow-root": {
                borderBottom: "1px solid #E5E7EB !important",
              },
              "& .MuiTableRow-root > .MuiTableCell-root": {
                borderBottom: "1px solid #E5E7EB !important",
              },
              "& .filename-column": {
                borderBottom: "1px solid #E5E7EB !important",
              },
            }}
          >
            <TableHeader
              {...{
                displayColumns: displayColumns,
                deasyFileKey: deasyFileKey,
                loading: loading,
                totalRows: totalRows,
                handleRefresh: handleRefresh,
                inputSearchQuery: inputSearchQuery,
                setInputSearchQuery: setInputSearchQuery,
                setPage: setPage,
                setSearchQuery: setSearchQuery,
                handleFilterClick: handleFilterClick,
                effectiveFilters: backendFilters,
                renderDistributionChart: renderDistributionChart,
                onStandardizeClick: handleStandardizePanelState,
                onFindReplaceClick: openFindReplace,
                availableColumns: effectiveAvailableColumns,
                setAvailableColumns: setAvailableColumns,
                dataslice_id: dataslice_id,
              }}
            />
            <TableBody
              tableData={tableData}
              displayColumns={displayColumns}
              deasyFileKey={deasyFileKey}
              TABLE_COLUMN_WIDTHS={TABLE_COLUMN_WIDTHS}
              chunkState={{
                expandedRow,
                loadingChunks,
                chunkData,
                setExpandedRow,
                setLoadingChunks,
                setChunkData,
              }}
              evidenceState={{
                activeFullText,
                activeEvidence,
                showEvidence,
                toggleChunkText,
                handleFetchChunkEvidence,
                setShowEvidence,
                renderEvidenceRow,
                loadingEvidenceMap,
              }}
              processTagValue={processTagValue}
              deasyUserConfig={deasyUserConfig}
              onTagClick={handleTagClick}
              onAddFilter={handleAddFilterFromCell}
            />
          </Table>
        </TableContainer>

        {/* Sticky pagination */}
        <TablePagination
          component="div"
          rowsPerPageOptions={[25, 50, 75, 100]}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          count={totalRows}
          labelDisplayedRows={({ from, to }) =>
            tableData.length ? `${from}-${to} of ${totalRows}` : "0-0"
          }
          nextIconButtonProps={{ disabled: !hasMore || loading }}
          showFirstButton
          showLastButton
          sx={{
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            "& .MuiToolbar-root": {
              minHeight: "48px",
              padding: "0 16px",
            },
            "& .MuiTablePagination-select": {
              padding: "8px",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.875rem",
                color: "#374151",
              },
            "& .MuiTablePagination-actions": {
              marginLeft: "20px",
              "& .MuiIconButton-root": {
                padding: "8px",
                color: "#374151",
                "&.Mui-disabled": {
                  color: "#D1D5DB",
                },
              },
            },
          }}
        />
      </Paper>

      {/* Restore FilterMenu component */}
      <FilterMenu
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        activeColumn={activeColumn}
        effectiveFilters={backendFilters}
        onClearFilters={handleClearFilters}
        filterSearch={filterSearch}
        onFilterSearchChange={setFilterSearch}
        loadingValues={loadingValues}
        vector_db_config={vector_db_config}
        onFilterSelect={handleFilterSelect}
        onRemoveFilter={handleRemoveFilter}
      />

      {showCreateDataSlice && (
        <CreateDataSlice
          isOpen={showCreateDataSlice}
          onClose={() => setShowCreateDataSlice(false)}
          conditions={conditionsNew}
          fileCount={totalRows}
          onSuccess={(dataSlice) => {
            setSelectedDataSlice(dataSlice);
            setCurrentScreen(WorkflowType.DATA_SLICE_MANAGEMENT);
          }}
        />
      )}

      <DeleteMetadataConfirmationModal
        showConfirmModal={showDeleteModal}
        setShowConfirmModal={setShowDeleteModal}
        selectedTagsToDelete={tagsToDelete}
        deleteStats={deleteStats}
        isDeletionStatsLoading={isDeletionStatsLoading}
        vdbProfileName={vdbLastActiveProfileName}
        deasyApiKey={deasyUserConfig.deasyApiKey}
        setRefreshKey={setRefreshKey}
        setChunkData={setChunkData}
        setExpandedRow={setExpandedRow}
      />

      {/* File-level TagEvidence Modal */}
      {tagEvidence.isOpen && (
        <TagEvidence
          tagName={tagEvidence.tagName}
          value={tagEvidence.value}
          fileName={tagEvidence.fileName}
          onClose={handleCloseTagEvidence}
          isNested={true}
        />
      )}

      {/* Find & Replace Panel */}
      <FindReplacePanel
        isOpen={findReplacePanel.isOpen}
        onClose={closeFindReplace}
        column={findReplacePanel.column}
        onRefresh={() => setRefreshKey((prev) => prev + 1)}
        dataslice_id={dataslice_id}
      />

      {/* Query Bar */}
      {/* <QueryBar
        open={queryDialogOpen && !showCreateDataSlice}
        onClose={handleCloseQueryDialog}
        vdbProfileName={vdbLastActiveProfileName}
        deasyApiKey={deasyUserConfig.deasyApiKey}
        onApplyFilters={handleApplyQueryFilters}
        tagDistributions={filteredDistributions}
        tagSchemas={savedTags.filter((tag) =>
          displayColumns.includes(tag.name),
        )}
        totalRows={totalRows}
      /> */}
    </div>
  );
};

// Wrapper component that provides the ViewMetadataContext
const MetadataTable = (props) => {
  return (
    <ViewMetadataProvider>
      <MetadataTableInner {...props} />
    </ViewMetadataProvider>
  );
};

export default MetadataTable;
